import React, { useEffect, useState } from 'react';
import {
    Box,
    Grid,
    TextField,
    Button,
    Typography,
    Divider,
    Paper,
    Autocomplete,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import { addNewVisit } from 'reduxStore/addNewVisit/addNewVisitSlice';
import { useAppDispatch, useAppSelector } from 'reduxStore/hooks';
import { AddVisitPayload } from 'reduxStore/addNewVisit/Model';
import { useLocation, useNavigate } from 'react-router-dom';
import { tokenList } from 'reduxStore/tokenList/tokenListSlice';
import { rfIdList } from 'reduxStore/rfIdList/rfIdListSlice';

interface RfidOption {
    label: string;
    value: string;
}

interface VisitFormState {
    patientFirstName: string;
    patientMiddleName: string;
    patientLastName: string;
    tokenNumber: string;
    department: string;
    inTime: Dayjs | null;
    outTime: Dayjs | null;
    phoneNumber: string;
    patientIdCard: string;
    guardian1FirstName: string;
    guardian1LastName: string;
    guardian1Id: string;
    guardian1Rfid: string;
    guardian2FirstName: string;
    guardian2LastName: string;
    guardian2Id: string;
    guardian2Rfid: string;
    visitType: string | null;
}


const TokenDetails: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const tokenData = location.state?.tokenData;
    const [rfidOptions, setRfidOptions] = useState<RfidOption[]>([]);
    const rfIdListResponse = useAppSelector(
        (state) => state.rfIdListRes);
    const addVisitResponse = useAppSelector(
        (state) => state.addVisitRes);
    const departmentOptions = [
        'Pediatric Cardiology',
        'Pediatric Cardiac Surgery',
        'Mother & Child'
    ];

    useEffect(() => {
        if (rfIdListResponse?.response?.data) {
            const rfidOptions = rfIdListResponse?.response?.data.map(item => ({
                label: item.item_code,
                value: item.serial_no
            }));
            setRfidOptions(rfidOptions);
        }
    }, [rfIdListResponse]);

    useEffect(() => {
        if (addVisitResponse?.response?.data !== undefined) {
            navigate('/tokenlist');
            dispatch(tokenList({ doctype: 'Token' }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addVisitResponse]);

    useEffect(() => {
        dispatch(rfIdList({ doctype: 'Serial No' }));
    }, [dispatch]);

    const [form, setForm] = useState<VisitFormState>({
        patientFirstName: '',
        patientMiddleName: '',
        patientLastName: '',
        tokenNumber: '',
        department: '',
        inTime: dayjs(),
        outTime: dayjs(),
        phoneNumber: '',
        patientIdCard: '',
        guardian1FirstName: '',
        guardian1LastName: '',
        guardian1Id: '',
        guardian1Rfid: '',
        guardian2FirstName: '',
        guardian2LastName: '',
        guardian2Id: '',
        guardian2Rfid: '',
        visitType: 'New_Visit',
    });

    useEffect(() => {
        if (tokenData) {
            setForm({
                patientFirstName: tokenData.visitor_name || '',
                patientMiddleName: tokenData.visitor_middle_name || '',
                patientLastName: tokenData.visitor_last_name || '',
                tokenNumber: tokenData.token_number || '',
                department: tokenData.department || '',
                inTime: tokenData.in_time ? dayjs(tokenData.in_time) : dayjs(),
                outTime: tokenData.out_time ? dayjs(tokenData.out_time) : null,
                phoneNumber: tokenData.phone_number || '',
                patientIdCard: tokenData.patient_id_card_number || '',
                guardian1FirstName: tokenData.father__husband__guardian_1 || '',
                guardian1LastName: tokenData.father_last_name || '',
                guardian1Id: tokenData.guardian_1_id_card_number || '',
                guardian1Rfid: tokenData.guardian_1_rfid_number || '',
                guardian2FirstName: tokenData.mother__guardian_2 || '',
                guardian2LastName: tokenData.mother_guardian_2_last_name || '',
                guardian2Id: tokenData.guardian_2_id_card_number || '',
                guardian2Rfid: tokenData.guardian_2_rfid_number || '',
                visitType: tokenData.patient_type || 'New_Visit',
            });
        }
    }, [tokenData]);

    const handleChange =
        (field: keyof VisitFormState) =>
            (event: React.ChangeEvent<HTMLInputElement>) => {
                setForm({ ...form, [field]: event.target.value });
            };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        /* eslint-disable camelcase */
        const body = {
            data: {
                visitor_name: form.patientFirstName,
                visitor_middle_name: form.patientMiddleName,
                visitor_last_name: form.patientLastName,
                token_number: form.tokenNumber,
                department: form.department,
                in_time: dayjs(form.inTime).format('YYYY-MM-DD HH:mm:ss'),
                in_out: dayjs(form.outTime).format('YYYY-MM-DD HH:mm:ss'),
                phone_number: form.phoneNumber,
                patient_id_card_number: form.patientIdCard,
                father__husband__guardian_1: form.guardian1FirstName,
                father_last_name: form.guardian1LastName,
                guardian_1_id_card_number: form.guardian1Id,
                guardian_1_rfid_number: form.guardian1Rfid,
                patient_type: form.visitType ?? 'New_Visit',
                mother__guardian_2: form.guardian2FirstName,
                mother_guardian_2_last_name: form.guardian2LastName,
                guardian_2_rfid_number: form.guardian2Rfid,
                guardian_2_id_card_number: form.guardian2Id,
                doctype: 'Token' as const,
            }
        };
        const access = '3703265d357a211:5b86c8948ff269d';
        const payload: AddVisitPayload = {
            body,
            access,
        };

        dispatch(addNewVisit(payload));
    };

    return (
        <Paper
            elevation={2}
            sx={{
                maxWidth: 1400,
                mx: 'auto',
                my: 4,
            }}
        >
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    p: 4,
                }}
            >
                {/* Patient Information Section */}
                <Typography variant="h6" sx={{ mb: 3, color: 'primary.main', fontWeight: 600 }}>
                    Patient Information
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            required
                            label="Patient First Name"
                            value={form.patientFirstName}
                            onChange={handleChange('patientFirstName')}
                            size="small"
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            label="Patient Middle Name"
                            value={form.patientMiddleName}
                            onChange={handleChange('patientMiddleName')}
                            size="small"
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            label="Patient Last Name"
                            value={form.patientLastName}
                            onChange={handleChange('patientLastName')}
                            size="small"
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <Autocomplete
                            freeSolo
                            options={departmentOptions}
                            value={form.department}
                            onChange={(_event, newValue) => {
                                setForm({ ...form, department: newValue || '' });
                            }}
                            onInputChange={(_event, newInputValue) => {
                                setForm({ ...form, department: newInputValue });
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    required
                                    label="Department"
                                    size="small"
                                />
                            )}
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            required
                            label="Token Number"
                            value={form.tokenNumber}
                            onChange={handleChange('tokenNumber')}
                            size="small"
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            required
                            label="Phone Number"
                            value={form.phoneNumber}
                            onChange={handleChange('phoneNumber')}
                            size="small"
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            required
                            label="Patient ID Card Number"
                            value={form.patientIdCard}
                            onChange={handleChange('patientIdCard')}
                            size="small"
                            disabled
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 4 }} />

                {/* Visit Timing Section */}
                <Typography variant="h6" sx={{ mb: 3, color: 'primary.main', fontWeight: 600 }}>
                    Visit Timing
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={4}>
                        <DateTimePicker
                            label="IN Time *"
                            value={form.inTime}
                            onChange={(val) => setForm({ ...form, inTime: val })}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    size: 'small',
                                    required: true
                                }
                            }}
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <DateTimePicker
                            label="OUT Time"
                            value={form.outTime}
                            onChange={(val) => setForm({ ...form, outTime: val })}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    size: 'small'
                                }
                            }}
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 4 }} />

                {/* Guardian 1 Information Section */}
                <Typography variant="h6" sx={{ mb: 3, color: 'primary.main', fontWeight: 600 }}>
                    Guardian 1 Information
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            required
                            label="Father / Husband / Guardian 1 First Name"
                            value={form.guardian1FirstName}
                            onChange={handleChange('guardian1FirstName')}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            required
                            label="Father / Husband / Guardian 1 Last Name"
                            value={form.guardian1LastName}
                            onChange={handleChange('guardian1LastName')}
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            label="Guardian 1 ID Card Number"
                            value={form.guardian1Id}
                            onChange={handleChange('guardian1Id')}
                            size="small"
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <Autocomplete
                            options={rfidOptions}
                            value={rfidOptions.find(option => option.value === form.guardian1Rfid) || null}
                            onChange={(_event, newValue) => {
                                // newValue?.value will be the serial_no (e.g., "11625155")
                                setForm({ ...form, guardian1Rfid: newValue?.value || '' });
                            }}
                            getOptionLabel={(option) => option.label} // Display item_code (e.g., "OP63")
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    required
                                    label="Guardian 1 RFID Number"
                                    size="small"
                                />
                            )}
                            disabled
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 4 }} />

                {/* Guardian 2 Information Section */}
                <Typography variant="h6" sx={{ mb: 3, color: 'primary.main', fontWeight: 600 }}>
                    Guardian 2 Information (Optional)
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            label="Mother / Guardian 2 First Name"
                            value={form.guardian2FirstName}
                            onChange={handleChange('guardian2FirstName')}
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            label="Mother / Guardian 2 Last Name"
                            value={form.guardian2LastName}
                            onChange={handleChange('guardian2LastName')}
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            label="Guardian 2 ID Card Number"
                            value={form.guardian2Id}
                            onChange={handleChange('guardian2Id')}
                            size="small"
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <Autocomplete
                            options={rfidOptions}
                            value={rfidOptions.find(option => option.value === form.guardian2Rfid) || null}
                            onChange={(_event, newValue) => {
                                setForm({ ...form, guardian2Rfid: newValue?.value || '' });
                            }}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Guardian 2 RFID Number"
                                    size="small"
                                />
                            )}
                            disabled
                        />
                    </Grid>
                </Grid>

                {/* Action Buttons */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 2,
                        mt: 4,
                        pt: 3,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <Button
                        variant="outlined"
                        size="large"
                        sx={{ minWidth: 120 }}
                        onClick={() => navigate('/tokenlist')}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        type="submit"
                        size="large"
                        sx={{ minWidth: 120 }}
                    >
                        Save
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
};

export default TokenDetails;